import {
  Card,
  Stack,
  Title,
  Text,
  Group,
  Badge,
  Table,
  SimpleGrid,
  Skeleton,
  Progress,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { num } from '../lib/format';

interface OutlierSample {
  part_number: string;
  product_type: string | null;
  weight_lbs: number | null;
  dimensions: {
    length_in: number | null;
    width_in: number | null;
    height_in: number | null;
  } | null;
  note: string;
}
interface Quality {
  weight_outliers_heavy: OutlierSample[];
  dim_outliers_large: OutlierSample[];
  suspicious_zero: OutlierSample[];
  source_breakdown: { source_host: string; parts: number; avg_weight: number }[];
  coverage_by_mfg: {
    manufacturer: string;
    total: number;
    with_weight: number;
    with_full_dims: number;
  }[];
}

interface ImageStats {
  summary: {
    total_rows: number;
    unique_images: number;
    unique_parts: number;
    classified: number;
    useful: number;
    watermarked: number;
    parts_with_useful_image: number;
    parts_with_branded_only: number;
  };
  by_image_type: Array<{ image_type: string; n: number }>;
  by_confidence: Array<{ confidence: string; n: number }>;
  by_watermark_type: Array<{ watermark_type: string; n: number }>;
}

export default function QualityPage() {
  const q = useQuery({
    queryKey: ['metrics', 'quality'],
    queryFn: () => api<Quality>('/api/metrics/quality'),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  const images = useQuery({
    queryKey: ['images', 'stats'],
    queryFn: () => api<ImageStats>('/api/images/stats'),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });

  const loading = q.isLoading;

  return (
    <Stack gap="md">
      <Title order={2}>Data quality</Title>
      <Text c="dimmed" size="sm">
        Surfacing likely bad values so we know what to scrub before exposing downstream.
      </Text>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Group justify="space-between">
              <Title order={5}>Coverage by manufacturer</Title>
              <Badge variant="light">Top 15</Badge>
            </Group>
            <Table.ScrollContainer minWidth={500}>
              <Table striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Manufacturer</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Parts</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>w/ weight</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>w/ dims</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {loading
                    ? Array.from({ length: 10 }).map((_, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>
                            <Skeleton height={14} width="70%" />
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Skeleton height={14} width={50} ml="auto" />
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Skeleton height={14} width={50} ml="auto" />
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Skeleton height={14} width={50} ml="auto" />
                          </Table.Td>
                        </Table.Tr>
                      ))
                    : (q.data?.coverage_by_mfg ?? []).map((r) => (
                        <Table.Tr key={r.manufacturer}>
                          <Table.Td>{r.manufacturer}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{num(r.total)}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>{num(r.with_weight)}</Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            {num(r.with_full_dims)}
                          </Table.Td>
                        </Table.Tr>
                      ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Stack>
        </Card>

        <Card withBorder padding="md" radius="md">
          <Stack gap="xs">
            <Title order={5}>Source host breakdown</Title>
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Source</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Parts</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Avg weight (lb)</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading
                  ? Array.from({ length: 8 }).map((_, i) => (
                      <Table.Tr key={i}>
                        <Table.Td>
                          <Skeleton height={14} width="60%" />
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Skeleton height={14} width={50} ml="auto" />
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Skeleton height={14} width={50} ml="auto" />
                        </Table.Td>
                      </Table.Tr>
                    ))
                  : (q.data?.source_breakdown ?? []).map((r) => (
                      <Table.Tr key={r.source_host}>
                        <Table.Td>
                          <Text ff="monospace" size="sm">
                            {r.source_host}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>{num(r.parts)}</Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {r.avg_weight?.toFixed(2)}
                        </Table.Td>
                      </Table.Tr>
                    ))}
              </Table.Tbody>
            </Table>
          </Stack>
        </Card>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <ImageClassificationCard data={images.data} loading={images.isLoading} />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <OutlierCard
          title="Heaviest (>500 lb) — likely kit-packaged"
          items={q.data?.weight_outliers_heavy ?? []}
          loading={loading}
        />
        <OutlierCard
          title="Dimension > 60 in — check for unit-mix errors"
          items={q.data?.dim_outliers_large ?? []}
          loading={loading}
        />
        <OutlierCard
          title="Weight populated but zero or near-zero dims"
          items={q.data?.suspicious_zero ?? []}
          loading={loading}
        />
      </SimpleGrid>
    </Stack>
  );
}

const TYPE_COLOR: Record<string, string> = {
  photo: 'teal',
  diagram: 'indigo',
  label: 'orange',
  packaging: 'lime',
  screenshot: 'gray',
  logo: 'gray',
  other: 'dark',
};

const WATERMARK_COLOR: Record<string, string> = {
  branded: 'red',
  attribution: 'yellow',
  decorative: 'gray',
};

const CONFIDENCE_COLOR: Record<string, string> = {
  high: 'teal',
  medium: 'yellow',
  low: 'red',
};

function ImageClassificationCard({
  data,
  loading,
}: {
  data: ImageStats | undefined;
  loading: boolean;
}) {
  const s = data?.summary;
  const usefulPct = s && s.classified > 0 ? Math.round((s.useful / s.classified) * 100) : null;
  const watermarkPct =
    s && s.classified > 0 ? Math.round((s.watermarked / s.classified) * 100) : null;

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={5}>Image classification</Title>
          <Badge variant="light" color="pink">
            Gemini 2.5 Flash
          </Badge>
        </Group>
        <Text c="dimmed" size="xs">
          Quality of crowd-sourced part images. Branded watermarks flag images we may not want to
          surface as our canonical visual.
        </Text>
        {loading ? (
          <Stack gap="xs" mt="xs">
            <Skeleton height={14} />
            <Skeleton height={14} />
            <Skeleton height={14} />
          </Stack>
        ) : !s || s.classified === 0 ? (
          <Text c="dimmed" size="sm">
            No classifications yet — run scripts/classify-images.ts.
          </Text>
        ) : (
          <Stack gap="sm" mt="xs">
            <Group gap="lg" wrap="wrap">
              <StatPair label="Classified" value={num(s.classified)} />
              <StatPair label="Useful" value={`${num(s.useful)} (${usefulPct}%)`} />
              <StatPair label="Watermarked" value={`${num(s.watermarked)} (${watermarkPct}%)`} />
              <StatPair
                label="Parts covered"
                value={`${num(s.parts_with_useful_image)}`}
                sub={
                  s.parts_with_branded_only > 0
                    ? `${num(s.parts_with_branded_only)} only have branded-watermark images`
                    : undefined
                }
              />
            </Group>

            {data && data.by_image_type.length > 0 && (
              <StatBar label="By image type" total={s.classified} items={data.by_image_type.map(t => ({
                key: t.image_type,
                n: t.n,
                color: TYPE_COLOR[t.image_type] ?? 'gray',
              }))} />
            )}
            {data && data.by_watermark_type.length > 0 && (
              <StatBar label="Watermark breakdown" total={s.watermarked} items={data.by_watermark_type.map(t => ({
                key: t.watermark_type,
                n: t.n,
                color: WATERMARK_COLOR[t.watermark_type] ?? 'gray',
              }))} />
            )}
            {data && data.by_confidence.length > 0 && (
              <StatBar label="Confidence" total={s.classified} items={data.by_confidence.map(t => ({
                key: t.confidence,
                n: t.n,
                color: CONFIDENCE_COLOR[t.confidence] ?? 'gray',
              }))} />
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function StatPair({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="md" fw={700}>
        {value}
      </Text>
      {sub && (
        <Text size="xs" c="red.7">
          {sub}
        </Text>
      )}
    </Stack>
  );
}

function StatBar({
  label,
  total,
  items,
}: {
  label: string;
  total: number;
  items: Array<{ key: string; n: number; color: string }>;
}) {
  if (total === 0) {
    return null;
  }
  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {label}
        </Text>
      </Group>
      <Progress.Root size="lg" radius="sm">
        {items.map((it) => (
          <Progress.Section key={it.key} value={(it.n / total) * 100} color={it.color}>
            {(it.n / total) * 100 > 8 && (
              <Progress.Label>{`${it.key} ${Math.round((it.n / total) * 100)}%`}</Progress.Label>
            )}
          </Progress.Section>
        ))}
      </Progress.Root>
      <Group gap="sm">
        {items.map((it) => (
          <Group gap={4} key={it.key} wrap="nowrap">
            <Badge size="xs" color={it.color} variant="light">
              {it.key}
            </Badge>
            <Text size="xs" c="dimmed">
              {num(it.n)}
            </Text>
          </Group>
        ))}
      </Group>
    </Stack>
  );
}

function OutlierCard({
  title,
  items,
  loading,
}: {
  title: string;
  items: OutlierSample[];
  loading: boolean;
}) {
  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="xs">
        <Title order={5}>{title}</Title>
        <Table striped>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Part</Table.Th>
              <Table.Th>Type</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>W</Table.Th>
              <Table.Th>L×W×H</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Table.Tr key={i}>
                    <Table.Td>
                      <Skeleton height={12} width="80%" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={12} width="70%" />
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Skeleton height={12} width={40} ml="auto" />
                    </Table.Td>
                    <Table.Td>
                      <Skeleton height={12} width="60%" />
                    </Table.Td>
                  </Table.Tr>
                ))
              : items.slice(0, 10).map((p) => (
                  <Table.Tr key={p.part_number}>
                    <Table.Td>
                      <Text ff="monospace" size="xs">
                        {p.part_number}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{p.product_type ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text size="xs">{p.weight_lbs ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">
                        {p.dimensions?.length_in != null
                          ? `${p.dimensions.length_in}×${p.dimensions.width_in}×${p.dimensions.height_in}`
                          : '—'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
